import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { Trash2, Mail, Phone, Calendar, Check } from "lucide-react";
import { toast } from "sonner";

const ContactsManager = () => {
  const { t } = useLang();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/contacts");
      setContacts(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markRead = async (id) => {
    try {
      await api.patch(`/admin/contacts/${id}?status=read`);
      setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, status: "read" } : c)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("?")) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      setContacts((cs) => cs.filter((c) => c.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return <div className="text-white/50 text-sm">Loading...</div>;
  }

  if (!contacts.length) {
    return (
      <div className="bg-[#121217] border border-white/10 p-10 text-center text-white/50 text-sm" data-testid="contacts-empty">
        {t("admin.dashboard.contacts.empty")}
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-12 gap-4" data-testid="contacts-list">
      <div className="md:col-span-5 space-y-2 max-h-[70vh] overflow-y-auto pr-2">
        {contacts.map((c) => (
          <button
            key={c.id}
            onClick={() => {
              setSelected(c);
              if (c.status === "new") markRead(c.id);
            }}
            data-testid={`contact-row-${c.id}`}
            className={`w-full text-left bg-[#121217] border ${
              selected?.id === c.id ? "border-[#D4AF37]/60" : "border-white/10"
            } hover:border-[#D4AF37]/40 p-4 transition-colors`}
          >
            <div className="flex items-center justify-between">
              <div className="text-sm text-white font-medium truncate">{c.name}</div>
              {c.status === "new" && (
                <span className="text-[9px] tracking-[0.2em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-0.5">
                  NEW
                </span>
              )}
            </div>
            <div className="text-xs text-white/50 mt-1 truncate">{c.email}</div>
            <div className="text-[10px] text-white/30 mt-2">
              {new Date(c.created_at).toLocaleString()}
            </div>
          </button>
        ))}
      </div>

      <div className="md:col-span-7">
        {selected ? (
          <div className="bg-[#121217] border border-white/10 p-6 md:p-8 space-y-5" data-testid="contact-detail">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-2xl text-white">{selected.name}</h3>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mt-1">
                  {selected.event_type || "—"}
                </div>
              </div>
              <button
                onClick={() => remove(selected.id)}
                data-testid={`contact-delete-${selected.id}`}
                className="border border-red-900/50 text-red-400 hover:bg-red-900/20 px-3 py-2 text-xs flex items-center gap-2"
              >
                <Trash2 size={12} /> {t("admin.dashboard.contacts.delete")}
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-3 text-white/70">
                <Mail size={14} className="text-[#D4AF37]" /> {selected.email}
              </div>
              {selected.phone && (
                <div className="flex items-center gap-3 text-white/70">
                  <Phone size={14} className="text-[#D4AF37]" /> {selected.phone}
                </div>
              )}
              {selected.event_date && (
                <div className="flex items-center gap-3 text-white/70">
                  <Calendar size={14} className="text-[#D4AF37]" /> {selected.event_date}
                </div>
              )}
              <div className="flex items-center gap-3 text-white/70">
                <Check size={14} className="text-[#D4AF37]" /> {selected.status}
              </div>
            </div>

            <div className="border-t border-white/10 pt-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-3">
                Message
              </div>
              <p className="text-white/80 leading-relaxed whitespace-pre-wrap text-sm">
                {selected.message}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-[#121217] border border-white/10 p-10 text-center text-white/40 text-sm h-full flex items-center justify-center">
            ← Bir mesaj seçiniz
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactsManager;
